import { motion } from 'framer-motion';

interface Challenge {
  icon: string;
  title: string;
  progress: number;
  week: string;
  personName: string;
  personRole: string;
  description: string;
  canDo: string[];
  imageUrl: string;
}

const OngoingChallengesSection = () => {
  const challenges: Challenge[] = [
    {
      icon: 'ri-plant-line',
      title: '紅葉植樹場所の草刈り',
      progress: 60,
      week: 'Week 6 / 全10週',
      personName: '田中さん',
      personRole: '移住3年目',
      description: '地味だけど、これが全ての土台。足場ができると、次の挑戦が始まります。紅葉を植える場所を整備中です。',
      canDo: [
        '週末の草刈り作業（初心者OK）',
        '道具は貸し出します',
        '軽い作業からでも大丈夫'
      ],
      imageUrl: 'https://public.readdy.ai/ai/img_res/70ae573a0f4773e5ea4af7935814add3.jpg'
    },
    {
      icon: 'ri-restaurant-line',
      title: '焼き鳥屋プロジェクト',
      progress: 30,
      week: 'Week 3 / 全12週',
      personName: '山田さん',
      personRole: '起業準備中',
      description: '10月オープン目標。場所探し、メンバー募集中。「何か始めたい」人、集まれ。',
      canDo: [
        'メニュー開発会議への参加',
        '物件探しの同行',
        'SNS運用のお手伝い'
      ],
      imageUrl: 'https://readdy.ai/api/search-image?query=small%20japanese%20yakitori%20restaurant%20planning%20meeting%2C%20people%20discussing%20business%20ideas%20around%20table%2C%20cozy%20warm%20atmosphere%2C%20simple%20clean%20background%20for%20startup%20concept&width=800&height=600&seq=yakitori-project-v2&orientation=landscape'
    },
    {
      icon: 'ri-home-4-line',
      title: '古民家リノベーション',
      progress: 45,
      week: 'Week 5 / 全16週',
      personName: '佐藤さん',
      personRole: '元会社員、今大工見習い',
      description: '築80年の空き家を再生中。DIYで少しずつ。完璧じゃないけど、それがいい。',
      canDo: [
        '週末のDIY作業参加',
        '掃除・片付けのお手伝い',
        '進捗の記録・撮影'
      ],
      imageUrl: 'https://readdy.ai/api/search-image?query=old%20japanese%20traditional%20house%20renovation%20work%20in%20progress%2C%20people%20doing%20diy%20work%20together%2C%20natural%20wood%20interior%2C%20warm%20community%20atmosphere%2C%20simple%20clean%20background&width=800&height=600&seq=renovation-project-v2&orientation=landscape'
    },
    {
      icon: 'ri-seedling-line',
      title: '竹林整備プロジェクト',
      progress: 50,
      week: 'Week 8 / 全20週',
      personName: '鈴木さん',
      personRole: '週末だけ吉野人',
      description: '放置竹林を再生。竹を切り、道を作り、光を入れる。地道だけど、変化が見える。',
      canDo: [
        '竹林整備作業への参加',
        '切った竹の活用アイデア',
        '安全管理のサポート'
      ],
      imageUrl: 'https://readdy.ai/api/search-image?query=bamboo%20forest%20maintenance%20work%20in%20japan%2C%20volunteers%20clearing%20overgrown%20bamboo%20grove%2C%20natural%20sunlight%20filtering%20through%20trees%2C%20peaceful%20atmosphere%2C%20simple%20clean%20background&width=800&height=600&seq=bamboo-project-v2&orientation=landscape'
    },
    {
      icon: 'ri-building-line',
      title: '空き家活用プロジェクト',
      progress: 25,
      week: 'Week 2 / 全8週',
      personName: '伊藤さん',
      personRole: '地域おこし協力隊',
      description: '使われていない家を探し、持ち主と話し、可能性を探る。まだ始まったばかり。',
      canDo: [
        '空き家調査の同行',
        '所有者へのヒアリング補助',
        'データ整理のお手伝い'
      ],
      imageUrl: 'https://readdy.ai/api/search-image?query=japanese%20rural%20empty%20house%20survey%20work%2C%20people%20inspecting%20old%20vacant%20property%2C%20documentary%20style%20photography%2C%20natural%20light%2C%20simple%20clean%20background&width=800&height=600&seq=vacant-house-project-v2&orientation=landscape'
    },
    {
      icon: 'ri-community-line',
      title: 'コミュニティ拠点づくり',
      progress: 40,
      week: 'Week 4 / 全12週',
      personName: '高橋さん',
      personRole: '移住1年目',
      description: '人が集まる場所を作る。まだ形は見えないけど、「ここに来れば誰かいる」を目指して。',
      canDo: [
        '拠点デザインの意見出し',
        'イベント企画への参加',
        '家具・設備の調達サポート'
      ],
      imageUrl: 'https://readdy.ai/api/search-image?query=community%20gathering%20space%20in%20japanese%20village%2C%20people%20meeting%20and%20discussing%20in%20cozy%20interior%2C%20warm%20welcoming%20atmosphere%2C%20simple%20clean%20background&width=800&height=600&seq=community-space-project-v2&orientation=landscape'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6">
            いま動いている挑戦
          </h2>
          <p className="text-lg md:text-xl text-gray-700">
            完成形ではなく、進行中の途中経過を共有します
          </p>
        </motion.div>

        {/* Challenges Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {challenges.map((challenge, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-stone-50 rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={challenge.imageUrl}
                  alt={`${challenge.title}の進行状況`}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full">
                  <i className={`${challenge.icon} text-2xl text-amber-600 w-6 h-6 inline-flex items-center justify-center`} aria-hidden="true"></i>
                </div>
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {challenge.title}
                </h3>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">{challenge.week}</span>
                    <span className="text-sm font-bold text-amber-700">{challenge.progress}%完了</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${challenge.progress}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: 0.3 }}
                      className="bg-gradient-to-r from-amber-500 to-amber-600 h-full rounded-full"
                      role="progressbar"
                      aria-valuenow={challenge.progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${challenge.title}の進捗状況：${challenge.progress}%`}
                    ></motion.div>
                  </div>
                </div>

                {/* Person */}
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-xl text-amber-700" aria-hidden="true"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{challenge.personName}</p>
                    <p className="text-sm text-gray-600">{challenge.personRole}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-700 leading-relaxed mb-6">
                  {challenge.description}
                </p>

                {/* What You Can Do */}
                <div className="bg-white rounded-lg p-5 mb-6">
                  <h4 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-hand-heart-line text-xl text-amber-600 w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
                    参加できること
                  </h4>
                  <ul className="space-y-2">
                    {challenge.canDo.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <i className="ri-checkbox-circle-line text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Button */}
                <a
                  href="#gateway"
                  className="block w-full bg-amber-600 hover:bg-amber-700 text-white text-center px-6 py-3 rounded-lg font-bold transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                  aria-label={`${challenge.title}の詳細を見る`}
                >
                  詳細を見る・相談する
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-16"
        >
          <p className="text-lg md:text-xl text-gray-700 mb-6">
            「どれから参加すればいいか分からない」という方は、
          </p>
          <a
            href="/consultation"
            className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-bold text-xl underline underline-offset-4"
          >
            吉野なんでも相談所で相談してください
            <i className="ri-arrow-right-line text-2xl w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default OngoingChallengesSection;
